/**
 * Image Processing Utilities for Map Markers
 *
 * Handles:
 * - Rounded square single image markers
 * - Grid composite images for clusters (2-4 photos)
 * - Count badges for clusters
 */

import type { Map as MapboxMap } from 'mapbox-gl'

// Configuration constants
// Canvas sizes are larger for crisp rendering, displayed smaller via pixelRatio
export const IMAGE_CONFIG = {
  singleSize: 100,          // Canvas size for single image markers (displayed at 50px)
  singleDisplaySize: 50,    // Display size on map
  singlePixelRatio: 2,      // pixelRatio = singleSize / singleDisplaySize
  clusterSize: 160,         // Canvas size for cluster composites (displayed at 80px)
  clusterDisplaySize: 80,   // Display size on map
  clusterPixelRatio: 2,     // pixelRatio for clusters
  borderWidth: 4,           // White border width (scaled for 100px canvas, displays as 2px)
  cornerRadius: 8,          // ~8% corner radius for rounded squares (scaled)
  badgeRadius: 24,          // Count badge circle radius (scaled)
  badgeColor: '#2259ff',    // Blue badge background
  badgeFontSize: 20,        // Badge text size (scaled)
  gridGap: 4,               // Gap between images in grid (scaled)
} as const

/**
 * Process a single image into a rounded square with white border
 * Used for individual image markers on the map
 * Returns ImageData at canvas size - use pixelRatio when adding to map
 */
export function processImageToRoundedSquare(
  image: HTMLImageElement | ImageBitmap,
  size: number = IMAGE_CONFIG.singleSize,
  borderWidth: number = IMAGE_CONFIG.borderWidth,
  cornerRadius: number = IMAGE_CONFIG.cornerRadius
): ImageData {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.width = size
  canvas.height = size

  // Clear canvas
  ctx.clearRect(0, 0, size, size)

  // Draw white background with rounded corners (for border effect)
  ctx.fillStyle = 'white'
  ctx.beginPath()
  ctx.roundRect(0, 0, size, size, cornerRadius)
  ctx.fill()

  // Create clipping path for image (inset by border width)
  const innerSize = size - borderWidth * 2
  const innerRadius = Math.max(0, cornerRadius - borderWidth / 2)

  ctx.save()
  ctx.beginPath()
  ctx.roundRect(borderWidth, borderWidth, innerSize, innerSize, innerRadius)
  ctx.clip()

  // Draw image inside clipped area
  ctx.drawImage(image, borderWidth, borderWidth, innerSize, innerSize)
  ctx.restore()

  return ctx.getImageData(0, 0, size, size)
}

/**
 * Load an image from URL and return as HTMLImageElement
 */
export function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`))
    img.src = url
  })
}

/**
 * Generate a grid composite image for clusters
 * Shows 2-4 images in a grid layout with count badge
 */
export async function generateClusterComposite(
  imageUrls: string[],
  count: number,
  clusterSize: number = IMAGE_CONFIG.clusterSize
): Promise<ImageData> {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.width = clusterSize
  canvas.height = clusterSize

  const { borderWidth, cornerRadius, gridGap } = IMAGE_CONFIG

  // Clear canvas and draw white background
  ctx.clearRect(0, 0, clusterSize, clusterSize)
  ctx.fillStyle = 'white'
  ctx.beginPath()
  ctx.roundRect(0, 0, clusterSize, clusterSize, cornerRadius * 1.5)
  ctx.fill()

  // Load images (up to 4)
  const imagesToLoad = imageUrls.slice(0, 4)
  const loadedImages: (HTMLImageElement | null)[] = await Promise.all(
    imagesToLoad.map(url =>
      loadImage(url).catch(() => null) // Return null for failed loads
    )
  )

  // Filter out failed images
  const validImages = loadedImages.filter((img): img is HTMLImageElement => img !== null)

  if (validImages.length === 0) {
    // No valid images - draw placeholder
    drawPlaceholderGrid(ctx, clusterSize, borderWidth, cornerRadius)
  } else {
    // Draw grid of images
    drawImageGrid(ctx, validImages, clusterSize, borderWidth, cornerRadius, gridGap)
  }

  // Draw count badge
  drawCountBadge(ctx, count, clusterSize)

  return ctx.getImageData(0, 0, clusterSize, clusterSize)
}

/**
 * Draw a grid of images on canvas
 */
function drawImageGrid(
  ctx: CanvasRenderingContext2D,
  images: HTMLImageElement[],
  size: number,
  borderWidth: number,
  cornerRadius: number,
  gap: number
): void {
  const count = images.length
  const innerSize = size - borderWidth * 2
  const innerRadius = Math.max(0, cornerRadius - borderWidth / 2)

  // Calculate cell dimensions based on image count
  let cols: number
  let rows: number

  if (count === 1) {
    cols = 1
    rows = 1
  } else if (count === 2) {
    cols = 2
    rows = 1
  } else {
    cols = 2
    rows = 2
  }

  const cellWidth = (innerSize - gap * (cols - 1)) / cols
  const cellHeight = (innerSize - gap * (rows - 1)) / rows
  const cellRadius = innerRadius / 2

  images.forEach((img, index) => {
    if (index >= 4) return // Max 4 images

    const col = index % cols
    const row = Math.floor(index / cols)

    const x = borderWidth + col * (cellWidth + gap)
    const y = borderWidth + row * (cellHeight + gap)

    ctx.save()
    ctx.beginPath()
    ctx.roundRect(x, y, cellWidth, cellHeight, cellRadius)
    ctx.clip()

    // Draw image to fill cell (cover mode)
    const imgAspect = img.width / img.height
    const cellAspect = cellWidth / cellHeight

    let drawWidth: number
    let drawHeight: number
    let drawX: number
    let drawY: number

    if (imgAspect > cellAspect) {
      // Image is wider - fit height
      drawHeight = cellHeight
      drawWidth = cellHeight * imgAspect
      drawX = x - (drawWidth - cellWidth) / 2
      drawY = y
    } else {
      // Image is taller - fit width
      drawWidth = cellWidth
      drawHeight = cellWidth / imgAspect
      drawX = x
      drawY = y - (drawHeight - cellHeight) / 2
    }

    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
    ctx.restore()
  })
}

/**
 * Draw a placeholder grid when no images are available
 */
function drawPlaceholderGrid(
  ctx: CanvasRenderingContext2D,
  size: number,
  borderWidth: number,
  cornerRadius: number
): void {
  const innerSize = size - borderWidth * 2
  const innerRadius = Math.max(0, cornerRadius - borderWidth / 2)

  ctx.fillStyle = '#e5e7eb' // Gray placeholder
  ctx.beginPath()
  ctx.roundRect(borderWidth, borderWidth, innerSize, innerSize, innerRadius)
  ctx.fill()
}

/**
 * Draw count badge on the cluster image
 */
function drawCountBadge(
  ctx: CanvasRenderingContext2D,
  count: number,
  size: number
): void {
  const { badgeRadius, badgeColor, badgeFontSize } = IMAGE_CONFIG

  // Position badge at bottom-right corner
  const x = size - badgeRadius - 4
  const y = size - badgeRadius - 4

  // Draw badge background
  ctx.beginPath()
  ctx.arc(x, y, badgeRadius, 0, Math.PI * 2)
  ctx.fillStyle = badgeColor
  ctx.fill()

  // Draw badge border
  ctx.strokeStyle = 'white'
  ctx.lineWidth = 2
  ctx.stroke()

  // Draw count text
  ctx.fillStyle = 'white'
  ctx.font = `bold ${badgeFontSize}px -apple-system, BlinkMacSystemFont, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  const displayCount = count > 999 ? '999+' : String(count)
  ctx.fillText(displayCount, x, y)
}

/**
 * Generate Apple Photos-style cluster image
 * Shows single image with count badge at bottom-center and pointer tail
 */
export async function generateAppleStyleCluster(
  imageUrl: string,
  count: number,
  size: number = IMAGE_CONFIG.clusterSize
): Promise<ImageData> {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!

  const { borderWidth, cornerRadius } = IMAGE_CONFIG
  const pointerHeight = 12 // Height of the pointer/tail at bottom
  const badgeHeight = 28   // Height of the count badge area

  // Canvas includes space for pointer
  canvas.width = size
  canvas.height = size + pointerHeight

  // Clear canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  // Draw white background with rounded corners (main body)
  ctx.fillStyle = 'white'
  ctx.beginPath()
  ctx.roundRect(0, 0, size, size, cornerRadius * 1.5)
  ctx.fill()

  // Draw pointer/tail at bottom center
  ctx.beginPath()
  const pointerWidth = 16
  const centerX = size / 2
  ctx.moveTo(centerX - pointerWidth / 2, size - 2)
  ctx.lineTo(centerX, size + pointerHeight)
  ctx.lineTo(centerX + pointerWidth / 2, size - 2)
  ctx.closePath()
  ctx.fill()

  // Load and draw the image
  try {
    const img = await loadImage(imageUrl)

    const innerSize = size - borderWidth * 2
    const innerRadius = Math.max(0, cornerRadius * 1.5 - borderWidth / 2)

    ctx.save()
    ctx.beginPath()
    ctx.roundRect(borderWidth, borderWidth, innerSize, innerSize - badgeHeight, innerRadius)
    ctx.clip()

    // Draw image to fill (cover mode)
    const imgAspect = img.width / img.height
    const boxWidth = innerSize
    const boxHeight = innerSize - badgeHeight
    const boxAspect = boxWidth / boxHeight

    let drawWidth: number
    let drawHeight: number
    let drawX: number
    let drawY: number

    if (imgAspect > boxAspect) {
      drawHeight = boxHeight
      drawWidth = boxHeight * imgAspect
      drawX = borderWidth - (drawWidth - boxWidth) / 2
      drawY = borderWidth
    } else {
      drawWidth = boxWidth
      drawHeight = boxWidth / imgAspect
      drawX = borderWidth
      drawY = borderWidth - (drawHeight - boxHeight) / 2
    }

    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
    ctx.restore()
  } catch (error) {
    // Draw placeholder if image fails to load
    const innerSize = size - borderWidth * 2
    ctx.fillStyle = '#e5e7eb'
    ctx.beginPath()
    ctx.roundRect(borderWidth, borderWidth, innerSize, innerSize - badgeHeight, cornerRadius)
    ctx.fill()
  }

  // Draw count badge area at bottom (inside the main body)
  const badgeY = size - badgeHeight - borderWidth
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
  ctx.beginPath()
  ctx.roundRect(borderWidth, badgeY, size - borderWidth * 2, badgeHeight, [0, 0, cornerRadius, cornerRadius])
  ctx.fill()

  // Draw count text
  ctx.fillStyle = 'white'
  ctx.font = `bold 18px -apple-system, BlinkMacSystemFont, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  const displayCount = count > 9999 ? '9999+' : count.toLocaleString()
  ctx.fillText(displayCount, size / 2, badgeY + badgeHeight / 2)

  return ctx.getImageData(0, 0, canvas.width, canvas.height)
}

/**
 * Generate a hash from an array of URLs for cache keying
 */
export function hashImageUrls(urls: string[]): string {
  const combined = urls.sort().join('|')
  let hash = 0
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash |= 0 // Convert to 32-bit signed integer
  }
  return Math.abs(hash).toString(36)
}

/**
 * Create cluster image ID from cluster data
 */
export function createClusterImageId(clusterId: number, imageUrls: string[]): string {
  return `cluster-${clusterId}-${hashImageUrls(imageUrls)}`
}

/**
 * Add a processed image to the Mapbox map
 */
export async function addImageToMap(
  map: MapboxMap,
  imageId: string,
  imageData: ImageData,
  pixelRatio: number = 1
): Promise<void> {
  if (map.hasImage(imageId)) {
    return // Already added
  }

  map.addImage(imageId, imageData, { pixelRatio })
}

/**
 * Process and add a single image marker to the map
 */
export async function processAndAddSingleImage(
  map: MapboxMap,
  imageId: string,
  imageUrl: string
): Promise<void> {
  if (map.hasImage(imageId)) {
    return // Already processed
  }

  try {
    const img = await loadImage(imageUrl)
    const processed = processImageToRoundedSquare(img)
    map.addImage(imageId, processed, { pixelRatio: IMAGE_CONFIG.singlePixelRatio })
  } catch (error) {
    console.warn(`Failed to process image for marker: ${imageUrl}`, error)
  }
}

/**
 * Process and add a cluster composite image to the map
 */
export async function processAndAddClusterImage(
  map: MapboxMap,
  clusterId: number,
  imageUrls: string[],
  count: number
): Promise<string | null> {
  const imageId = createClusterImageId(clusterId, imageUrls)

  if (map.hasImage(imageId)) {
    return imageId // Already processed
  }

  try {
    const composite = await generateClusterComposite(imageUrls, count, IMAGE_CONFIG.clusterSize)
    map.addImage(imageId, composite, { pixelRatio: IMAGE_CONFIG.clusterPixelRatio })
    return imageId
  } catch (error) {
    console.warn(`Failed to create cluster composite for cluster ${clusterId}`, error)
    return null
  }
}
